import apicaller from '../../../utils/apicaller';
import api from '../../../main.api';
import constant from '../../../main.constants';

const actions = {
    async FETCH_JOB({ commit }, { jobId }) {
        if (!jobId) return null;
        commit('setCurrentJobLoading', true);
        try {
            const { data } = await apicaller.get(`${api.FACESEARCHJOB.getById}/${jobId}`);
            const job = data && data.result ? data.result : null;
            commit('setCurrentJob', job);
            return job;
        } catch (e) {
            console.log('FETCH_JOB error', e);
            return null;
        } finally {
            commit('setCurrentJobLoading', false);
        }
    },
    START_JOB_POLL({ commit, dispatch, state, getters }, { jobId }) {
        commit('clearPollHandle');
        commit('setCurrentJob', { id: jobId, status: constant.FACE_JOB_STATUS.PENDING, fileCount: 0, processedCount: 0 });
        dispatch('FETCH_JOB', { jobId });
        const handle = setInterval(async () => {
            await dispatch('FETCH_JOB', { jobId: state.currentJobId });
            if (getters.isJobTerminal) {
                commit('clearPollHandle');
            }
        }, constant.FACE_JOB_POLL_INTERVAL_MS);
        commit('setPollHandle', handle);
    },
    STOP_JOB_POLL({ commit }) {
        commit('clearPollHandle');
    },
    async FETCH_JOBS({ commit }, params) {
        commit('setJobsListLoading', true);
        try {
            const { data } = await apicaller.get(api.FACESEARCHJOB.mine, { params });
            const raw = data && data.result ? data.result : null;

            let items = [];
            let total = 0;

            if (raw) {
                items = raw.items || raw.jobs || [];

                if (typeof raw.total === 'number') {
                    total = raw.total;
                } else {
                    total = items.length;
                }
            }

            const result = { items, total };
            commit('setJobsList', result);
            return result;
        } catch (e) {
            console.log('FETCH_JOBS error', e);
            commit('setJobsList', { items: [], total: 0 });
            return { items: [], total: 0 };
        } finally {
            commit('setJobsListLoading', false);
        }
    },
    async UPLOAD_VIDEO({ commit, dispatch }, { formData }) {
        commit('setVideoUploadInFlight', true);
        try {
            const { data } = await apicaller.post(api.FACEVIDEOUPLOAD.upload, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const result = data && data.result ? data.result : null;
            if (result && result.uploadId) {
                dispatch('START_VIDEO_POLL', { uploadId: result.uploadId });
            }
            return result;
        } catch (e) {
            console.log('UPLOAD_VIDEO error', e);
            return null;
        } finally {
            commit('setVideoUploadInFlight', false);
        }
    },
    async FETCH_VIDEO_UPLOAD({ commit }, { uploadId }) {
        if (!uploadId) return null;
        commit('setVideoCurrentUploadLoading', true);
        try {
            const { data } = await apicaller.get(`${api.FACEVIDEOUPLOAD.status}/${uploadId}/status`);
            const upload = data && data.result ? data.result : null;
            commit('setVideoCurrentUpload', upload);
            return upload;
        } catch (e) {
            console.log('FETCH_VIDEO_UPLOAD error', e);
            return null;
        } finally {
            commit('setVideoCurrentUploadLoading', false);
        }
    },
    START_VIDEO_POLL({ commit, dispatch, state, getters }, { uploadId }) {
        commit('clearVideoPollHandle');
        commit('setVideoCurrentUpload', {
            uploadId,
            status: constant.FACE_VIDEO_JOB_STATUS.PENDING,
            totalFrames: 0,
            processedFrames: 0,
            facesDetected: 0,
            facesMatched: 0,
            coPresenceEvents: 0,
            frames: []
        });
        dispatch('FETCH_VIDEO_UPLOAD', { uploadId });
        const handle = setInterval(async () => {
            await dispatch('FETCH_VIDEO_UPLOAD', { uploadId: state.videoCurrentUploadId });
            if (getters.isVideoJobTerminal) {
                commit('clearVideoPollHandle');
            }
        }, constant.FACE_VIDEO_POLL_INTERVAL_MS);
        commit('setVideoPollHandle', handle);
    },
    STOP_VIDEO_POLL({ commit }) {
        commit('clearVideoPollHandle');
    },
    async FETCH_VIDEO_UPLOADS({ commit }, params) {
        commit('setVideoUploadsListLoading', true);
        try {
            const { data } = await apicaller.get(api.FACEVIDEOUPLOAD.mine, { params });
            const raw = data && data.result ? data.result : null;

            let items = [];
            let total = 0;

            if (raw) {
                items = raw.items || raw.uploads || [];

                if (typeof raw.total === 'number') {
                    total = raw.total;
                } else {
                    total = items.length;
                }
            }

            const result = { items, total };
            commit('setVideoUploadsList', result);
            return result;
        } catch (e) {
            console.log('FETCH_VIDEO_UPLOADS error', e);
            commit('setVideoUploadsList', { items: [], total: 0 });
            return { items: [], total: 0 };
        } finally {
            commit('setVideoUploadsListLoading', false);
        }
    },

    FETCH_SCENARIOS({ commit }, payload = {}) {
        // silent=true -> progress poll'unda loading state'i toggle etme (tablo flicker'ini onle).
        const silent = payload && payload.silent;
        if (!silent) commit('setScenariosListLoading', true);
        return apicaller.get(api.FACESCENARIO.system)
            .then(({ data }) => {
                const items = data && data.result ? data.result : [];
                commit('setScenariosList', items);
                return items;
            })
            .catch(e => {
                console.log('FETCH_SCENARIOS error', e);
                if (!silent) commit('setScenariosList', []);
                return [];
            })
            .finally(() => {
                if (!silent) commit('setScenariosListLoading', false);
            });
    },
    FETCH_SCENARIO_RUNS(_ctx, { scenarioId, limit = 50 }) {
        return apicaller.get(`${api.FACESCENARIO.base}/${scenarioId}/runs`, { params: { limit } })
            .then(({ data }) => (data && data.result ? data.result : []))
            .catch(e => {
                console.log('FETCH_SCENARIO_RUNS error', e);
                return [];
            });
    },
    UPDATE_SCENARIO_PARAMS(_ctx, { scenarioId, payload }) {
        return apicaller.put(`${api.FACESCENARIO.base}/${scenarioId}`, payload)
            .then(({ data }) => ({ ok: data && data.statusCode === 200, message: data && data.message, result: data && data.result }))
            .catch(e => {
                console.log('UPDATE_SCENARIO_PARAMS error', e);
                return { ok: false, message: null };
            });
    },
    SET_SCENARIO_ACTIVE(_ctx, { scenarioId, isActive }) {
        const verb = isActive ? 'activate' : 'deactivate';
        return apicaller.post(`${api.FACESCENARIO.base}/${scenarioId}/${verb}`)
            .then(({ data }) => ({ ok: data && data.statusCode === 200, message: data && data.message }))
            .catch(e => {
                console.log('SET_SCENARIO_ACTIVE error', e);
                return { ok: false, message: null };
            });
    },
    RUN_SCENARIO(_ctx, { scenarioId }) {
        return apicaller.post(`${api.FACESCENARIO.base}/${scenarioId}/run`)
            .then(({ data }) => ({ ok: data && data.statusCode === 200, message: data && data.message, result: data && data.result }))
            .catch(e => {
                console.log('RUN_SCENARIO error', e);
                return { ok: false, message: null };
            });
    },
    RUN_INITIAL_TC_SCAN() {
        return apicaller.post(api.FACEADMIN.runInitialTcScan)
            .then(({ data }) => ({ ok: data && data.statusCode === 200, message: data && data.message, result: data && data.result }))
            .catch(e => {
                console.log('RUN_INITIAL_TC_SCAN error', e);
                return { ok: false, message: null };
            });
    },
    //  aylik force re-scan guvenlik agi — imleci 0'a cek + mode=full drain (arka plan, aninda doner).
    RUN_FORCE_RESCAN() {
        return apicaller.post(api.FACEADMIN.runForceRescan)
            .then(({ data }) => ({ ok: data && data.statusCode === 200, message: data && data.message, result: data && data.result }))
            .catch(e => {
                console.log('RUN_FORCE_RESCAN error', e);
                return { ok: false, message: null };
            });
    },
    FETCH_ALARMS({ commit }, params) {
        commit('setAlarmsListLoading', true);
        return apicaller.get(api.FACEALARM.list, { params })
            .then(({ data }) => {
                const raw = data && data.result ? data.result : null;
                let items = [];
                let total = 0;
                if (raw) {
                    items = Array.isArray(raw) ? raw : (raw.items || []);
                    total = typeof raw.total === "number" ? raw.total : items.length;
                }
                const result = { items, total };
                commit('setAlarmsList', result);
                return result;
            })
            .catch(e => {
                console.log('FETCH_ALARMS error', e);
                commit('setAlarmsList', { items: [], total: 0 });
                return { items: [], total: 0 };
            })
            .finally(() => {
                commit('setAlarmsListLoading', false);
            });
    },
    // Kanit fotograflari: alarm kimlikno'lari -> sahisresim (arama sayfasi deseni; milvus_id kullanilmaz).
    // Drawer acilinca lazy cagrilir; state'e yazilmaz, bilesen-lokal tutulur (FETCH_SCENARIO_RUNS deseni).
    FETCH_ALARM_PHOTOS(_ctx, { alarmId }) {
        return apicaller.get(`${api.FACEALARM.base}/${alarmId}/photos`)
            .then(({ data }) => (data && data.result ? data.result : null))
            .catch(e => {
                console.log('FETCH_ALARM_PHOTOS error', e);
                return null;
            });
    },
    CONFIRM_ALARM(_ctx, { alarmId, reason }) {
        return apicaller.post(`${api.FACEALARM.base}/${alarmId}/confirm`, { reason })
            .then(({ data }) => ({ ok: data && data.statusCode === 200, statusCode: data && data.statusCode, message: data && data.message }))
            .catch(e => {
                console.log('CONFIRM_ALARM error', e);
                return { ok: false, message: null };
            });
    },
    DISMISS_ALARM(_ctx, { alarmId, reason }) {
        return apicaller.post(`${api.FACEALARM.base}/${alarmId}/dismiss`, { reason })
            .then(({ data }) => ({ ok: data && data.statusCode === 200, statusCode: data && data.statusCode, message: data && data.message }))
            .catch(e => {
                console.log('DISMISS_ALARM error', e);
                return { ok: false, message: null };
            });
    },
    FETCH_SYSTEM_CONFIG({ commit }) {
        commit('setSystemConfigLoading', true);
        return apicaller.get(api.FACESYSTEMCONFIG.get)
            .then(({ data }) => {
                const items = data && data.result ? data.result : [];
                commit('setSystemConfig', items);
                return items;
            })
            .catch(e => {
                console.log('FETCH_SYSTEM_CONFIG error', e);
                commit('setSystemConfig', []);
                return [];
            })
            .finally(() => {
                commit('setSystemConfigLoading', false);
            });
    },
    UPDATE_SYSTEM_CONFIG(_ctx, { configKey, value }) {
        return apicaller.put(`${api.FACESYSTEMCONFIG.base}/${configKey}`, { value })
            .then(({ data }) => ({ ok: data && data.statusCode === 200, message: data && data.message }))
            .catch(e => {
                console.log('UPDATE_SYSTEM_CONFIG error', e);
                return { ok: false, message: null };
            });
    },
    FETCH_EXCLUSIONS({ commit }, params) {
        commit('setExclusionsListLoading', true);
        return apicaller.get(api.FACEMATCHFEEDBACK.exclusion, { params })
            .then(({ data }) => {
                const items = data && data.result ? data.result : [];
                commit('setExclusionsList', items);
                return items;
            })
            .catch(e => {
                console.log('FETCH_EXCLUSIONS error', e);
                commit('setExclusionsList', []);
                return [];
            })
            .finally(() => {
                commit('setExclusionsListLoading', false);
            });
    },
    FETCH_PERSON_DETAIL({ commit }, { kimlikno }) {
        if (!kimlikno) return Promise.resolve(null);
        commit('setPersonDetailLoading', true);
        return apicaller.get(`${api.FACEPERSON.base}/${kimlikno}`)
            .then(({ data }) => {
                const person = data && data.result ? data.result : null;
                commit('setPersonDetail', person);
                return person;
            })
            .catch(e => {
                console.log('FETCH_PERSON_DETAIL error', e);
                commit('setPersonDetail', null);
                return null;
            })
            .finally(() => {
                commit('setPersonDetailLoading', false);
            });
    },
    FETCH_PERSON_MATCH_HISTORY({ commit }, { kimlikno, limit = 50, offset = 0 }) {
        if (!kimlikno) return Promise.resolve({ items: [], total: 0 });
        commit('setPersonMatchHistoryLoading', true);
        return apicaller.get(`${api.FACEPERSON.base}/${kimlikno}/match-history`, { params: { limit, offset } })
            .then(({ data }) => {
                const raw = data && data.result ? data.result : null;
                const items = raw ? (raw.items || []) : [];
                if (offset === 0) commit('setPersonMatchHistory', items);
                return { items, total: raw && typeof raw.total === 'number' ? raw.total : items.length };
            })
            .catch(e => {
                console.log('FETCH_PERSON_MATCH_HISTORY error', e);
                return { items: [], total: 0 };
            })
            .finally(() => {
                commit('setPersonMatchHistoryLoading', false);
            });
    },
    // Birliktelik (timeline + iliski agi) — mevcut GET /FaceCoPresence/kimlikno/{kimlikno} reuse.
    FETCH_CO_PRESENCE({ commit }, { kimlikno, limit = 100, offset = 0 }) {
        if (!kimlikno) return Promise.resolve(null);
        commit('setCoPresenceLoading', true);
        return apicaller.get(`${api.FACECOPRESENCE.base}/kimlikno/${kimlikno}`, { params: { limit, offset } })
            .then(({ data }) => {
                const result = data && data.result ? data.result : null;
                commit('setCoPresence', result);
                commit('setCoPresenceKimlikno', kimlikno);
                return result;
            })
            .catch(e => {
                console.log('FETCH_CO_PRESENCE error', e);
                commit('setCoPresence', null);
                commit('setCoPresenceKimlikno', null);
                return null;
            })
            .finally(() => {
                commit('setCoPresenceLoading', false);
            });
    },
    DELETE_EXCLUSION(_ctx, { id }) {
        return apicaller.delete(`${api.FACEMATCHFEEDBACK.exclusion}/${id}`)
            .then(({ data }) => ({ ok: data && data.statusCode === 200, message: data && data.message }))
            .catch(e => {
                console.log('DELETE_EXCLUSION error', e);
                return { ok: false, message: null };
            });
    }
};

export default actions;